import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { motion, AnimatePresence, useAnimationControls } from 'framer-motion';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'root/src/client/v2/common/components/ui/tooltip';
import { cn } from 'root/src/client/v2/common/helpers/cn';
// Animated red-tinted error message box. Renders nothing when there is no message.
export const FieldError = ({ children, className }) => (<AnimatePresence>
		{children && (<motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.15, ease: 'easeOut' }} className={cn('rounded-lg border border-solid border-destructive/20 bg-destructive/10 px-3 py-2 text-[11px] text-destructive', className)}>
				{children}
			</motion.p>)}
	</AnimatePresence>);
const SHAKE = { x: [0, -5, 5, -5, 5, 0], transition: { duration: 0.35 } };
// Bumped each time a form fails validation; a `Field` that currently has an error shakes when it
// changes (so only the offending fields shake, and they re-shake on every failed submit). Default 0
// means "no shaking" - forms that don't wrap their fields in `FieldShakeProvider` are unaffected.
const FieldShakeContext = createContext(0);
export const FieldShakeProvider = FieldShakeContext.Provider;
// For a form: `const { shakeKey, triggerShake } = useFieldShake()`; call `triggerShake()` from the
// react-hook-form invalid handler and wrap the fields in `<FieldShakeProvider value={shakeKey}>`.
export const useFieldShake = () => {
    const [shakeKey, setShakeKey] = useState(0);
    const triggerShake = useCallback(() => setShakeKey(key => key + 1), []);
    return { shakeKey, triggerShake };
};
// Label + control + animated error message. Use for redesign form fields. Shakes when its form fails
// validation while this field has an error (driven by `FieldShakeProvider`).
export const Field = ({ label, htmlFor, error, info, className, children }) => {
    const shakeKey = useContext(FieldShakeContext);
    const controls = useAnimationControls();
    const hasError = Boolean(error);
    useEffect(() => {
        if (shakeKey > 0 && hasError) {
            void controls.start(SHAKE);
        }
    }, [shakeKey]);
    return (<motion.div animate={controls} className={cn('space-y-1.5', className)}>
			{label && (<div className="flex items-center gap-1.5">
					<label htmlFor={htmlFor} className="text-[11px] font-medium text-muted-foreground">{label}</label>
					{info && (<TooltipProvider delayDuration={150}>
							<Tooltip>
								<TooltipTrigger asChild>
									<button type="button" tabIndex={-1} aria-label="Field info" className="inline-flex shrink-0 text-muted-foreground/40 transition-colors hover:text-muted-foreground">
										<InfoOutlinedIcon sx={{ fontSize: 13 }}/>
									</button>
								</TooltipTrigger>
								<TooltipContent className="max-w-[240px] text-xs font-normal leading-relaxed">{info}</TooltipContent>
							</Tooltip>
						</TooltipProvider>)}
				</div>)}
			{children}
			<FieldError>{error}</FieldError>
		</motion.div>);
};
